/**
 * Account Page Theme
 * Styling configuration for Account page (profile, addresses, etc.)
 */

const baseStyle = {
  // Page layout
  base: {
    maxWidth: '100%',
    paddingTop: 0,
    width: '100%',
  },
  contentContainer: {
    maxWidth: '1440px',
    mx: 'auto',
    paddingBottom: {base: '80px', lg: '120px'},
    paddingTop: {base: '32px', lg: '64px'},
    paddingX: {base: '20px', lg: '80px'},
    position: 'relative',
    overflow: 'hidden',
    sx: {
      '&&.account-myaccount-container': {
        paddingTop: {base: '16px', lg: '64px'},
        paddingBottom: {base: '0', lg: '40px'},
      },
      '&&.account-profile-container, &&.account-preferences-container': {
        overflow: 'visible',
      },
      '&&.account-myhistory-container': {
        paddingBottom: {base: '80px', lg: '120px'},
      },
      '&&.account-order-detail-container': {
        '& .confirmation-line-item__strike-price': {
          fontSize: {base: '12px', lg: '12px'},
        },
      },
      '& .chakra-grid > *': {
        minWidth: 0,
      },
      '& h1': {
        color: 'brandColors.greyscale90',
        fontSize: {base: '32px', lg: '40px'},
        fontWeight: '500',
        lineHeight: '120%',
      },
      '& .page-alert': {
        alignItems: 'center',
        paddingBottom: 2,
        paddingTop: 2,
        scrollMarginTop: {base: '8px', lg: '16px'},
        transform: 'translateY(-100%)',
        width: {base: '100%', lg: 'auto'},
      },
      '& .page-alert-close-button .chakra-icon, & .page-alert-close-button': {
        height: {base: '16px', lg: '20px'},
        width: {base: '16px', lg: '20px'},
      },
      '& .order-detail-summary': {
        marginTop: {base: '16px', lg: '32px'},
        '& .chakra-heading': {
          fontSize: {base: '24px', lg: '32px'},
          fontWeight: '500',
          lineHeight: {base: '150%', lg: '140%'},
        },
      },
      '& .chakra-button__icon': {
        marginEnd: 0,
        marginInlineEnd: 0,
        mr: 0,
      },
      '& .confirmation-line-item__content': {
        gap: {base: '6px', lg: '6px'},
      },
      '& .order-detail-summary-meta-date': {
        fontSize: {base: '16px', lg: '16px'},
        fontWeight: {base: '500', lg: '600'},
        lineHeight: {base: '160%', lg: '140%'},
      },
      '& .order-detail-summary-totals-savings-row': {
        color: 'secondary.newSaleRed',
      },
      '& .confirmation-line-item__resource-links': {
        marginTop: {base: '10px', lg: '10px'},
        whiteSpace: 'nowrap',
        gap: {base: '6px', lg: '6px'},
        '& .chakra-link': {
          fontSize: {base: '13px', lg: '12px'},
          fontWeight: {base: '400', lg: '600'},
          lineHeight: {base: '1.2', lg: '1.2'},
          color: {base: 'greyscale90Alpha.90', lg: 'greyscale90Alpha.90'},
        },
        '& .chakra-icon': {
          color: 'brandColors.primaryDarkLinen',
        },
      },
      '& .account-order-details-page': {
        maxWidth: {base: '100%', lg: '507px'},
      },
      '& .order-detail-field-row .account-order-detail__field-label': {
        color: 'greyscale.70',
      },
      '& .account-order-detail-delivery': {
        '& .chakra-heading': {
          fontSize: {base: '24px', lg: '32px'},
          fontWeight: '500',
          lineHeight: {base: '150%', lg: '140%'},
        },
        '& .chakra-stack': {
          gap: {base: '0', lg: '0'},
        },
        '& .order-detail-field-row:last-of-type': {
          borderBottom: '1px solid',
          borderColor: 'brandColors.greyscale10',
        },
      },
      '& .account-order-detail-contact-billing': {
        '& .chakra-heading': {
          fontSize: {base: '24px', lg: '32px'},
          fontWeight: '500',
          lineHeight: {base: '150%', lg: '140%'},
        },
        '& .order-detail-field-row:last-of-type': {
          borderBottom: 'none',
        },
      },
      '& .order-detail-summary-total-price-row': {
        '& .chakra-text': {
          fontSize: {base: '16px', lg: '18px'},
          fontWeight: '500',
          lineHeight: {base: '160%', lg: '160%'},
        },
      },
    },
  },
  pageContentContainer: {
    position: 'relative',
  },

  // Hero section
  heroContainer: {
    position: 'relative',
    sx: {
      '.hero-content-wrapper': {
        display: 'none',
      },
      '.hero-image-wrapper': {
        height: '100%',
      },
      '.hero-banner': {
        minHeight: {base: '100%', lg: '100%'},
        height: {base: '234px', lg: '535px'},
      },
    },
  },
  heroContent: {
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxWidth: '1440px',
    mx: 'auto',
    paddingX: {base: '20px', lg: '80px'},
  },
  heroContentWrapper: {
    bottom: 0,
    left: 0,
    paddingBottom: {base: '25px', lg: '64px'},
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  heroGreeting: {
    fontSize: {base: '16px', lg: '32px'},
    fontWeight: '500',
  },
  heroGreetingUserName: {
    fontSize: {base: '40px', lg: '60px'},
    fontWeight: '500',
    letterSpacing: '-0.6px',
    lineHeight: '120%',
  },

  // Generic form card (shared by profile and addresses)
  formCard: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
  },
  formHeading: {
    fontSize: 'lg',
    marginBottom: 4,
  },
  formStack: {
    spacing: 6,
  },
  formAlert: {
    status: 'error',
  },
  formAlertIcon: {
    boxSize: 4,
    color: 'red.500',
  },
  formAlertText: {
    fontSize: 'sm',
    ml: 3,
  },
  formButtonContainer: {
    display: 'flex',
    flexWrap: {base: 'wrap', lg: 'nowrap'},
    gap: 4,
  },
  formButton: {
    fontWeight: '600',
    minWidth: {base: 'auto', lg: '135px'},
    minHeight: '54px',
    width: 'auto',
  },
  formButtonMobileOneRow: {
    flex: {base: '1 1 100%', lg: '0 0 auto'},
    width: {base: '100%', lg: 'auto'},
  },
  formProfileButton: {
    fontWeight: '600',
    minHeight: '54px',
    minWidth: {base: 'auto', lg: '188px'},
    width: {base: '100%', lg: 'auto'},
  },
  helperText: {
    color: 'brandColors.greyscale70',
    fontSize: {base: '13px', lg: '12px'},
    flexBasis: '100%',
    lineHeight: '1.2',
    paddingX: '18px',
    marginTop: '8px',
  },

  // Address list section
  sectionStack: {
    spacing: 4,
  },
  sectionHeading: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '32px', lg: '40px'},
    fontWeight: '500',
    lineHeight: '120%',
  },

  // Address grid
  addressGrid: {
    columns: [1, 2, 2, 2, 3],
    gridAutoFlow: 'row dense',
    spacing: 4,
  },

  // Add address button
  addButton: {
    backgroundColor: 'transparent',
    border: '1px dashed',
    borderColor: 'brandColors.primaryDarkLinen',
    color: 'greyscale90Alpha.90',
    fontWeight: '600',
    fontSize: {base: '14px', lg: '16px'},
    lineHeight: '120%',
    height: 'auto',
    padding: '24px 20px',
    marginBottom: 4,
    rounded: 'base',
    variant: 'outline',
    width: '100%',
    sx: {
      '&.has-no-addresses': {
        marginBottom: 8,
      },
    },
  },
  addButtonIcon: {
    boxSize: '15px',
    color: 'brandColors.primaryDarkLinen',
    display: 'block',
  },

  // Default badge (top right of card: dark bg, white uppercase text)
  defaultBadge: {
    bg: 'brandColors.greyscale90',
    color: 'white',
    fontSize: {base: '13px', lg: '12px'},
    fontWeight: '600',
    position: 'absolute',
    right: 4,
    top: 4,
    textTransform: 'normal',
    paddingX: 2,
    paddingY: 1,
    rounded: 'base',
    variant: 'solid',
  },

  // Saved address card (white, light grey border, rounded – default and normal)
  savedAddressCard: {
    backgroundColor: 'white',
    border: '1.5px solid',
    borderColor: 'brandColors.greyscale10',
    padding: 4,
    position: 'relative',
    rounded: '4px',
  },

  // Address card name (bold, dark, larger)
  addressCardName: {
    color: 'brandColors.greyscale',
    fontSize: {base: '14x', lg: '16px'},
    fontWeight: '600',
    lineHeight: '120%',
    marginBottom: 1,
  },

  // Address card company name (optional, below name)
  addressCardCompany: {
    color: 'brandColors.greyscale70',
    fontSize: 'sm',
    fontWeight: 'normal',
    lineHeight: '120%',
    marginBottom: 2,
  },

  // Address card address lines (regular, smaller, each on new line)
  addressCardAddress: {
    color: 'brandColors.greyscale80',
    fontSize: {base: '14x', lg: '16px'},
    fontWeight: 'normal',
    lineHeight: '140%',
  },

  // Address card actions (Edit/Remove at bottom left)
  addressCardActions: {
    display: 'flex',
    gap: 8,
    marginTop: 3,
  },

  // Address card action link (Edit/Remove – brown/gold, underlined)
  addressActionLink: {
    color: 'brandColors.primaryDarkLinen',
    fontSize: {base: '14px', lg: '16px'},
    fontWeight: {base: '600', lg: '400'},
    height: 'auto',
    padding: {base: 0, lg: 0},
    textDecoration: 'underline',
    textUnderlineOffset: '5px',
  },
  addressActionLinkRemove: {
    color: 'brandColors.primaryDarkLinen',
    fontSize: {base: '14px', lg: '16px'},
    fontWeight: {base: '600', lg: '400'},
    height: 'auto',
    padding: {base: 0, lg: 0},
    textDecoration: 'underline',
  },

  // Address form container
  addressFormContainer: {
    backgroundColor: 'transparent',
    padding: 0,
    position: 'relative',
  },

  // Address form heading
  addressFormHeadingModal: {
    marginBottom: 6,
    size: 'md',
  },

  // Addresses section (for scroll margin with hash navigation)
  addressesSection: {
    scrollMarginTop: '100px',
    gap: '32px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: {base: '100%', lg: '510px'},
    position: 'relative',
  },

  // No address state
  noAddressContainer: {},
  noAddressTitle: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '16px', lg: '18px'},
    fontWeight: '500',
    lineHeight: '160%',
    marginBottom: 2,
  },
  noAddressHint: {
    color: 'brandColors.greyscale70',
    fontSize: {base: '14px', lg: '16px'},
  },

  // Address cards grid
  addressCardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  addressCardItem: {
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: 'brandColors.greyscale10',
    padding: '16px',
    position: 'relative',
    rounded: 'base',
    width: '100%',
  },

  // Page heading
  pageHeading: {
    fontSize: '24px',
  },

  // Add button responsive width
  addButtonResponsive: {
    marginBottom: 4,
  },

  // Account nav link
  accountNavLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  accountNavLink: {
    backgroundColor: 'transparent',
    border: '2px solid transparent',
    borderRadius: 0,
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    fontSize: '18px',
    fontWeight: '500',
    height: 'auto',
    lineHeight: '160%',
    textDecoration: 'none',
    textUnderlineOffset: '15px',
    textDecorationThickness: '2px',
    transitionProperty: 'none',
    sx: {
      '&': {
        padding: '8px 0',
      },
      '&:hover, &:focus-visible': {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '2px',
        textDecorationColor: 'brandColors.primaryDarkLinen',
      },
    },
  },
  accountNavLinkActive: {
    sx: {
      '&, &[data-active="true"]': {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'black',
        padding: '8px 0',
        textDecoration: 'underline',
        textDecorationThickness: '2px',
        textDecorationColor: 'brandColors.primaryDarkLinen',
      },
    },
  },

  // Profile form dirty notification
  profileDirtyNotification: {
    color: 'brandColors.primaryDarkLinen',
    fontSize: '12px',
    lineHeight: '1.2',
    paddingLeft: 4,
    marginTop: -3,
  },

  // Preferences (Email/SMS) section
  preferencesPageContainer: {
    gap: '32px',
    maxWidth: {base: '100%', lg: '510px'},
    position: 'relative',
  },
  preferencesFormStack: {
    color: 'brandColors.greyscale90',
    spacing: 8,
  },
  preferencesSkeletonStack: {
    spacing: 6,
  },
  preferencesSection: {
    marginBottom: 0,
  },
  preferencesSectionHeading: {
    fontSize: {base: '20px', lg: '24px'},
    fontWeight: '500',
    lineHeight: '150%',
    marginBottom: {base: '32px', lg: '16px'},
  },
  preferencesDescription: {
    marginBottom: '18px',
  },
  preferencesHelperText: {
    color: 'brandColors.greyscale70',
    marginTop: '18px',
  },
  preferencesHelperTextDisabled: {
    color: 'brandColors.greyscale50',
    marginTop: '18px',
  },
  preferencesCheckbox: {
    color: 'brandColors.greyscale',
    sx: {
      '.chakra-checkbox__label': {
        fontSize: {base: '14px', lg: '16px'},
      },
    },
  },
  preferencesFormSubmitButton: {
    fontSize: {base: '14px', lg: '18px'},
    fontWeight: {base: '600', lg: '500'},
    lineHeight: {base: '140%', lg: '160%'},
    minHeight: '54px',
    minWidth: {base: '100%', lg: '243px'},
  },
  profilePageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: {base: '100%', lg: '510px'},
    position: 'relative',
  },
  pageAlertContainer: {
    display: 'flex',
    justifyContent: {base: 'stretch', lg: 'center'},
    left: 0,
    position: 'absolute',
    right: 0,
    top: '-6px',
    width: '100%',
  },
  profileFormContainer: {
    marginBottom: '40px',
  },

  // ACCOUNT-02 — My Account landing
  landingStack: {
    minWidth: 0,
    spacing: 0,
    width: '100%',
  },
  landingSuggestedSlot: {
    mt: {base: '48px', lg: '64px'},
    minWidth: 0,
    pb: {base: 10, lg: 0},
    width: {base: 'calc(100% + 40px)', lg: '100%'},
    marginLeft: {base: '-20px', lg: '0'},
    marginRight: {base: '-20px', lg: '0'},
    sx: {
      // ACCOUNT-02 — ProductTiles + RangeProductTile style overrides (Figma 5759:50662 / 56821)
      // Logic unchanged — RangeProductTile as-is; only scoped CSS here.
      '& .component--product-tiles': {
        gap: {base: 6, lg: 8},
        maxW: '100%',
        mx: 0,
        pb: 0,
        pl: 0,
        pr: 0,
        pt: 0,
        width: '100%',
        overflow: 'unset',
      },
      '& .contentful-product-tiles__carousel--account-landing': {
        paddingLeft: {base: '20px', lg: '0'},
      },
      '& .contentful-product-tiles__root': {
        gap: {base: 6, lg: 8},
        maxW: '100%',
        mx: 0,
        pb: 0,
        pl: 0,
        pr: 0,
        pt: 0,
        width: '100%',
      },
      '& .account-landing__suggested-title': {
        color: 'brandColors.greyscale90',
        fontSize: {base: '32px', lg: '40px'},
        fontWeight: '500',
        letterSpacing: {base: '-0.32px', lg: '-0.4px'},
        lineHeight: '120%',
      },
      '& .contentful-product-tiles__root > .chakra-stack': {
        px: 0,
        width: '100%',
      },
      '& .contentful-product-tiles__carousel': {
        paddingLeft: '80px',
        paddingRight: '80px',
        maxWidth: 'calc(100% + 160px)',
        width: 'calc(100% + 160px)',
        marginLeft: '-80px',
        overflow: {base: 'hidden', lg: 'hidden'},
      },
      '& .contentful-product-tiles__swiper': {
        maxW: '100%',
        minWidth: 0,
        overflow: {base: 'hidden', lg: 'visible'},
        paddingLeft: {base: '20px', lg: '0'},
      },

      // --- RangeProductTile overrides (account landing carousel only) ---
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-badges"]': {
        display: 'none',
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-image"] .chakra-aspect-ratio': {
        maxW: '100%',
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-image"]': {
        bg: 'brandColors.secondaryBGTranslucent15',
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-info"]': {
        gap: '16px',
        minWidth: 0,
        pb: '16px',
        pt: '16px',
        px: 0,
        width: '100%',
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile__copy': {
        gap: {base: '12px', lg: '8px'},
        minWidth: 0,
        width: '100%',
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile__title': {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '2',
        color: 'grayAlpha.90',
        display: '-webkit-box',
        fontFamily: 'body',
        fontSize: {base: '16px', lg: '18px'},
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,
        maxW: '100%',
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile__subtitle': {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '1',
        color: 'grayAlpha.70',
        display: '-webkit-box',
        fontFamily: 'body',
        fontSize: {base: '13px', lg: '12px'},
        fontWeight: 400,
        lineHeight: 1.2,
        maxW: '100%',
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile-swatches + span, & .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-swatches"] span:last-child': {
        fontSize: {base: '13px', lg: '12px'},
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-info"] > div:last-of-type > div:first-of-type': {
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '8px',
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile__price-current': {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: '16px',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: {base: '500', lg: '600'},
        lineHeight: 1.4,
      },
      '& .contentful-product-tiles__carousel--account-landing .range-product-tile__price-current--on-sale': {
        color: 'secondary.newSaleRed',
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-info"] > div:last-of-type > div:first-of-type > div': {
        alignItems: 'center',
        display: 'inline-flex',
        flexDirection: 'row',
        gap: 0,
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-info"] > div:last-of-type > div:first-of-type > div span': {
        color: 'grayAlpha.70',
        fontFamily: 'body',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 1.2,
      },
      '& .contentful-product-tiles__carousel--account-landing [data-testid="range-product-tile-info"] > div:last-of-type > div:first-of-type > div span:last-of-type': {
        marginLeft: '3px',
        textDecoration: 'line-through',
      },
    },
  },
  landingGetInspiredSlot: {
    bg: 'rgba(255, 255, 255, 0.7)',
    maxWidth: '100%',
    minWidth: 0,
    overflow: {base: 'hidden', lg: 'visible'},
    pb: {base: '64px', lg: '99px'},
    pt: {base: '64px', lg: '80px'},
    width: '100%',
    sx: {
      '& .chakra-heading': {
        paddingLeft: {base: '20px', lg: '0'},
        paddingRight: {base: '20px', lg: '0'},
      },
      '& .swiper-account-landing-get-inspired': {
        paddingLeft: {base: '20px', lg: '0'},
        paddingRight: {base: '20px', lg: '0'},
      },
      // ACCOUNT-02 — BlogRelatedArticles full viewport width; 1.2 mobile / 3-up desktop
      '& [class*="component--blog-related-articles"]': {
        gap: {base: 6, lg: 10},
        maxW: '1280px',
        minWidth: 0,
        mx: 'auto',
        pl: 0,
        pr: 0,
        py: 0,
        width: '100%',
      },
      '& [class*="component--blog-related-articles"] h2': {
        color: '#262626',
        fontSize: {base: '32px', lg: '40px'},
        fontWeight: '500',
        letterSpacing: {base: '-0.32px', lg: '-0.4px'},
        lineHeight: '120%',
      },
      '& .swiper': {
        maxWidth: '100%',
        minWidth: 0,
        overflow: {base: 'hidden', lg: 'visible'},
        width: '100%',
      },
      '& .swiper .swiper-slide': {
        boxSizing: 'border-box',
        height: 'auto',
        width: 'auto',
      },
      '& .blog-summary__title': {
        paddingX: {base: 0, lg: 0},
        fontSize: {base: '16px', lg: '18px'},
        fontWeight: 500,
        lineHeight: '160%',
      },
      '& .blog-summary__tag-text': {
        fontSize: {base: '13px', lg: '12px'},
      },
    },
  },
  latestOrderSection: {
    width: '100%',
  },
  latestOrderHeading: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '32px', lg: '40px'},
    fontWeight: '500',
    letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    lineHeight: '120%',
    margin: 0,
  },
  latestOrderEmptyStack: {
    alignItems: 'flex-start',
    gap: {base: '24px', lg: 8},
    width: '100%',
    marginBottom: {base: '16px', lg: 0},
  },
  latestOrderEmptyCopy: {
    gap: 4,
    width: '100%',
  },
  latestOrderEmptyTitle: {
    color: 'brandColors.greyscale90',
    fontSize: '24px',
    fontWeight: '500',
    lineHeight: '140%',
    margin: 0,
  },
  latestOrderEmptyDescription: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '14px', lg: '16px'},
    fontWeight: '400',
    lineHeight: '140%',
    maxW: '100%',
  },
  latestOrderShopNowButton: {
    alignSelf: {base: 'stretch', lg: 'flex-start'},
    fontSize: {base: '18px', lg: '18px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: '500',
    height: '64px',
    lineHeight: '120%',
    minH: '64px',
    minW: {base: '100%', lg: 'auto'},
    px: {base: 6, lg: 12},
    textDecoration: 'none',
    width: {base: '100%', lg: 'auto'},
    sx: {
      '&:hover, &:focus, &:active': {
        textDecoration: 'none',
      },
    },
  },
  latestOrderCard: {
    bg: 'background.secondary',
    borderRadius: 'base',
    overflow: 'hidden',
    width: '100%',
  },
  latestOrderCardHeader: {
    alignItems: 'center',
    bg: {base: '#EBE6DA', md: 'background.secondary'},
    display: 'flex',
    gap: {base: 0, md: 6},
    height: {base: '51px', md: 'auto'},
    justifyContent: {base: 'space-between', md: 'flex-start'},
    minH: {base: '51px', md: '51px'},
    px: {base: '24px', md: 8},
    py: {base: '15px', md: '15px'},
    width: '100%',
    sx: {
      borderBottom: {
        base: 'none',
        md: '1px solid rgba(27, 26, 24, 0.10)',
      },
    },
  },
  latestOrderCardHeaderMeta: {
    alignItems: 'center',
    flexWrap: {base: 'nowrap', md: 'wrap'},
    gap: {base: 0, md: 6},
    justifyContent: {base: 'space-between', md: 'flex-start'},
    width: {base: '100%', md: 'auto'},
  },
  latestOrderCardHeaderDateGroup: {
    alignItems: 'center',
    gap: '5px',
  },
  latestOrderOrderedOnText: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '14px', md: '16px'},
    fontWeight: '400',
    lineHeight: '140%',
  },
  latestOrderOrderedOnDate: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '14px', md: '18px'},
    fontWeight: {base: '600', md: '500'},
    lineHeight: {base: '140%', md: '160%'},
    whiteSpace: 'nowrap',
  },
  latestOrderCardBody: {
    bg: 'background.secondary',
    px: {base: 6, md: 8},
    py: {base: '25px', md: '25px'},
    width: '100%',
  },
  latestOrderDetailGridDesktop: {
    alignItems: 'stretch',
    gap: 4,
    width: '100%',
  },
  latestOrderDetailGridMobile: {
    gap: 6,
    width: '100%',
  },
  latestOrderDetailRowMobile: {
    alignItems: 'stretch',
    gap: 4,
    width: '100%',
  },
  latestOrderDetailCell: {
    alignItems: 'flex-start',
    gap: 1,
    minW: 0,
    textAlign: 'left',
    width: '100%',
  },
  latestOrderDetailLabel: {
    color: 'greyscale.70',
    fontSize: {base: '13px', md: '12px'},
    fontWeight: '400',
    lineHeight: '1.2',
  },
  latestOrderDetailValue: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '16px', md: '16px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: {base: '500', md: '600'},
    lineHeight: {base: '160%', md: '1.4'},
    wordBreak: 'break-word',
  },
  latestOrderCardFooter: {
    alignItems: {base: 'stretch', md: 'flex-start'},
    gap: {base: '31px', md: 6},
    width: '100%',
  },
  latestOrderSummary: {
    width: '100%',
  },
  latestOrderMetaText: {
    color: 'brandColors.greyscale80',
    fontSize: {base: '14px', lg: '16px'},
    fontWeight: '400',
    lineHeight: '140%',
  },
  latestOrderDetailsButton: {
    alignSelf: {base: 'stretch', md: 'flex-start'},
    flexShrink: 0,
    fontSize: {base: '14px', md: '18px'},
    fontWeight: {base: '600', md: '500'},
    lineHeight: '160%',
    minH: '54px',
    minW: {base: '100%', md: '211px'},
    px: {base: 6, md: 12},
    textDecoration: 'none',
    width: {base: '100%', md: '211px'},
    color: '#000',
    borderColor: '#000',
    sx: {
      '&:hover, &:focus, &:active': {
        textDecoration: 'none',
      },
    },
  },
  latestOrderThumbnails: {
    flex: {md: '1 1 auto'},
    minW: 0,
    overflowX: 'auto',
    width: {base: '100%', md: 'auto'},
  },
  orderHistoryPage: {
    width: '100%',
  },
  orderHistoryEmpty: {
    alignItems: 'flex-start',
    gap: {base: 16, lg: 18},
    width: '100%',
  },
  orderHistoryEmptyCopy: {
    gap: 4,
    width: '100%',
  },
  orderHistoryEmptyTitle: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '20px', lg: '24px'},
    fontWeight: '500',
    lineHeight: {base: '150%', lg: '140%'},
    margin: 0,
  },
  orderHistoryEmptyDescription: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '14px', lg: '16px'},
    fontWeight: '400',
    lineHeight: '140%',
    maxW: '100%',
  },
  orderHistoryEmptyShopNowButton: {
    alignSelf: {base: 'stretch', lg: 'flex-start'},
    fontSize: {base: '18px', lg: '18px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: '500',
    height: '64px',
    lineHeight: '120%',
    minH: '64px',
    minW: {base: '100%', lg: 'auto'},
    px: {base: 6, lg: 12},
    textDecoration: 'none',
    width: {base: '100%', lg: 'auto'},
    sx: {
      '&:hover, &:focus, &:active': {
        textDecoration: 'none',
      },
    },
  },
  orderHistoryContactSection: {
    gap: 4,
    width: '100%',
  },
  orderHistoryContactHeading: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '20px', lg: '24px'},
    fontWeight: '500',
    lineHeight: {base: '150%', lg: '140%'},
    margin: 0,
  },
  orderHistoryEmptyActions: {
    alignItems: {base: 'stretch', sm: 'center'},
    direction: {base: 'column', sm: 'row'},
    gap: 3,
    width: '100%',
  },
  orderHistoryHeading: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '32px', lg: '40px'},
    fontWeight: '500',
    letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    lineHeight: '120%',
    margin: 0,
  },
  orderHistoryContactButton: {
    alignSelf: {base: 'stretch', lg: 'flex-start'},
    fontSize: {base: '18px', lg: '18px'},
    fontWeight: '500',
    height: '64px',
    lineHeight: '160%',
    minH: '64px',
    minW: {base: '100%', lg: 'auto'},
    px: {base: 6, lg: 12},
    textDecoration: 'none',
    width: {base: '100%', lg: 'auto'},
    sx: {
      '&:hover, &:focus, &:active': {
        textDecoration: 'none',
      },
    },
  },
  orderHistoryShowOlderButton: {
    alignSelf: 'center',
    color: 'greyscale90Alpha.90',
    fontSize: {base: '18px', lg: '18px'},
    fontWeight: '500',
    gap: 2,
    lineHeight: '160%',
    minH: 'auto',
    px: 0,
    py: 2,
    marginTop: '8px',
    _hover: {
      color: 'greyscale90Alpha.90',
      textDecoration: 'underline',
    },
    sx: {
      '& .chakra-button__icon': {
        marginStart: 0,
        marginInlineStart: 0,
        mr: 0,
        color: 'greyscale90Alpha.90',
      },
      '& svg': {
        color: 'greyscale90Alpha.90',
        width: '18px',
      },
    },
  },
  orderHistoryFaqSlot: {
    width: '100%',
    sx: {
      '& .contentful-accordion-panel': {
        maxWidth: 'none',
        padding: 0,
      },
      '& .contentful-accordion-panel__header': {
        marginBottom: 8,
      },
      '& .contentful-accordion-panel__title, & .component--accordion-panel h2': {
        color: 'black',
        fontFamily: 'body',
        fontSize: {base: '32px', lg: '40px'},
        fontWeight: '500',
        letterSpacing: {base: '-0.32px', lg: '-0.4px'},
        lineHeight: '120%',
        margin: 0,
      },
      '& .contentful-accordion-panel__toggle-all': {
        display: 'none',
      },
      '& .contentful-accordion-panel__content': {
        gap: 0,
      },
      '& .contentful-accordion': {
        '& .contentful-accordion__item': {
          borderTop: {base: 'none', lg: '1px solid'},
          borderBottom: {base: '1px solid', lg: 'none'},
          borderColor: {base: 'brandColors.greyscale10', lg: 'brandColors.greyscale10'},
          
        },
        '&:first-child': {
          '& .contentful-accordion__item': {
            borderTop: {base: '1px solid', lg: '1px solid'},
            borderColor: {base: 'brandColors.greyscale10', lg: 'brandColors.greyscale10'},
          }
        },
        '& .component--accordion:last-child': {
          '& .contentful-accordion__item': {
            borderTop: 'none',
            borderBottom: {base: '1px solid', lg: 'none'},
            borderColor: {base: 'brandColors.greyscale10', lg: 'brandColors.greyscale10'},
          },
        },
        '& .contentful-accordion__button': {
          color: 'black',
          px: 0,
          py: 6,
          _hover: {
            backgroundColor: 'transparent',
          },
        },
        '& .contentful-accordion__title': {
          color: 'black',
          flex: 1,
          fontFamily: 'body',
          fontSize: {base: '16px', lg: '18px'},
          fontVariantNumeric: 'lining-nums proportional-nums',
          fontWeight: '500',
          lineHeight: '160%',
          textAlign: 'left',
        },
        '& .contentful-accordion__icon': {
          alignItems: 'center',
          color: '#8C683B',
          display: 'flex',
          flexShrink: 0,
          height: '16px',
          justifyContent: 'center',
          marginLeft: 4,
          width: '16px',
          '& svg': {
            color: '#8C683B',
          },
        },
        '& .contentful-accordion__panel': {
          color: 'black',
          pb: 6,
          pl: 0,
          pr: 6,
          pt: 0,
        },
        '& .contentful-accordion__body, & .contentful-accordion__panel .rich-text-content, & .contentful-accordion__panel p, & .contentful-accordion__panel li, & .contentful-accordion__panel .amb-markup, & .contentful-accordion__panel .chakra-text':
          {
            color: 'black',
            fontFamily: 'body',
            fontSize: {base: '14px', lg: '16px'},
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '140%',
          },
        '& .contentful-accordion__panel a, & .contentful-accordion__panel .chakra-link': {
          color: 'black',
        },
      },
    },
  },
  orderDetailPage: {
    alignItems: 'flex-start',
    width: '100%',
  },
  orderDetailTitleStatusStack: {
    width: '100%',
  },
  orderDetailBackLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    color: 'primary.darkLinen',
    fontSize: {base: '16px', lg: '18px'},
    fontWeight: '500',
    gap: '14px',
    justifyContent: 'flex-start',
    lineHeight: '160%',
    minWidth: 'auto',
    padding: 0,
    paddingLeft: 0,
    pl: 0,
    pr: 0,
    px: 0,
    textDecoration: 'none',
    width: 'auto',
    '& .chakra-button__icon': {
      marginEnd: 0,
      marginInlineEnd: 0,
      mr: 0,
    },
    _hover: {color: 'primary.darkLinen', textDecoration: 'none'},
  },
  orderDetailTitle: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '32px', lg: '40px'},
    fontWeight: '500',
    letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    lineHeight: '120%',
    margin: 0,
  },
  orderDetailStatusBanner: {
    bg: 'brandColors.secondaryBGTranslucent15',
    gap: 0,
    paddingX: {base: '24px', lg: '24px'},
    paddingY: {base: '24px', lg: '21px'},
    height: {base: '74px', lg: '61px'},
    width: '100%',
  },
  orderDetailStatusRow: {
    alignItems: 'center',
    gap: 2,
    width: '100%',
  },
  orderDetailStatusDot: {
    bg: 'brandColors.primaryDarkLinen',
    border: '2px solid',
    borderColor: 'brandColors.secondaryBG',
    borderRadius: 'full',
    flexShrink: 0,
    h: '12px',
    w: '12px',
  },
  orderDetailStatusDotDelivered: {
    bg: 'brandColors.success',
    border: '2px solid',
    borderColor: 'brandColors.secondaryBG',
    borderRadius: 'full',
    flexShrink: 0,
    h: '12px',
    w: '12px',
  },
  orderDetailStatusLabel: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '16px', lg: '16px'},
    fontWeight: {base: '500', lg: '600'},
    lineHeight: {base: '160%', lg: '140%'},
  },
  orderDetailStatusDate: {
    color: 'brandColors.greyscale90',
    fontSize: {base: '13px', lg: '12px'},
    fontWeight: '400',
    lineHeight: '120%',
  },
};

const Account = {
  baseStyle,
  parts: [
    // Page layout
    'base',
    'contentContainer',
    'pageContentContainer',
    // Hero
    'heroContainer',
    'heroContentWrapper',
    'heroContent',
    'heroGreeting',
    'heroGreetingUserName',
    // Generic form
    'formCard',
    'formHeading',
    'formStack',
    'formAlert',
    'formAlertIcon',
    'formAlertText',
    'formButtonContainer',
    'formButton',
    'formButtonMobileOneRow',
    'formProfileButton',
    'helperText',
    // Address section
    'sectionStack',
    'sectionHeading',
    'addressGrid',
    'addButton',
    'addButtonIcon',
    'defaultBadge',
    // Saved address card
    'savedAddressCard',
    'addressCardName',
    'addressCardCompany',
    'addressCardAddress',
    'addressCardActions',
    'addressActionLink',
    'addressActionLinkRemove',
    'addressFormContainer',
    'addressFormHeadingModal',
    'addressesSection',
    // No address state
    'noAddressContainer',
    'noAddressTitle',
    'noAddressHint',
    // Address cards grid
    'addressCardsGrid',
    // Address card
    'addressCardItem',
    // Page heading
    'pageHeading',
    // Add button responsive
    'addButtonResponsive',
    // Account nav
    'accountNavLinks',
    'accountNavLink',
    'accountNavLinkActive',
    // Profile form
    'profileDirtyNotification',
    // Preferences
    'preferencesFormStack',
    'preferencesSkeletonStack',
    'preferencesSection',
    'preferencesSectionHeading',
    'preferencesDescription',
    'preferencesHelperText',
    'preferencesHelperTextDisabled',
    'preferencesCheckbox',
    'profilePageContainer',
    'pageAlertContainer',
    'profileFormContainer',
    // Landing (ACCOUNT-02)
    'landingStack',
    'landingSuggestedSlot',
    'landingGetInspiredSlot',
    'latestOrderSection',
    'latestOrderHeading',
    'latestOrderEmptyStack',
    'latestOrderEmptyCopy',
    'latestOrderEmptyTitle',
    'latestOrderEmptyDescription',
    'latestOrderShopNowButton',
    'latestOrderCard',
    'latestOrderCardHeader',
    'latestOrderCardHeaderMeta',
    'latestOrderCardHeaderDateGroup',
    'latestOrderOrderedOnText',
    'latestOrderOrderedOnDate',
    'latestOrderCardBody',
    'latestOrderDetailGridDesktop',
    'latestOrderDetailGridMobile',
    'latestOrderDetailRowMobile',
    'latestOrderDetailCell',
    'latestOrderDetailLabel',
    'latestOrderDetailValue',
    'latestOrderCardFooter',
    'latestOrderSummary',
    'latestOrderMetaText',
    'latestOrderDetailsButton',
    'latestOrderThumbnails',
    // Order history (ACCHIST-04)
    'orderHistoryPage',
    'orderHistoryHeading',
    'orderHistoryEmpty',
    'orderHistoryEmptyCopy',
    'orderHistoryEmptyTitle',
    'orderHistoryEmptyDescription',
    'orderHistoryEmptyShopNowButton',
    'orderHistoryContactSection',
    'orderHistoryContactHeading',
    'orderHistoryEmptyActions',
    'orderHistoryContactButton',
    'orderHistoryShowOlderButton',
    'orderHistoryFaqSlot',
    // Order detail (ACCHIST / account order detail)
    'orderDetailPage',
    'orderDetailTitleStatusStack',
    'orderDetailBackLink',
    'orderDetailTitle',
    'orderDetailStatusBanner',
    'orderDetailStatusRow',
    'orderDetailStatusDot',
    'orderDetailStatusDotDelivered',
    'orderDetailStatusLabel',
    'orderDetailStatusDate',
  ],
};

export default Account;
